import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, FormControl } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Page from "~/components/Page";
import Loader from '~/components/Loader';
import ExternalLink from '~/components/ExternalLink';
import ClientCreateProjectModal from "~/modals/ClientCreateProjectModal";
import ProjectsTable from "~/tables/ProjectsTable";
import ArchivedProjectsTable from "~/tables/ArchivedProjectsTable";
import { DOCUMENTATION_LINK, ENABLE_USER_PROJECTS, MAX_USER_PROJECTS } from "~/const";
import { apiGetProjects, apiToggleProjectArchivation } from "~/providers/api";
import { IClientProject } from "~/providers/client-interfaces";
import TableControls from "~/components/TableControls";
import { getLink } from "~/providers/route";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useTrans } from "~/hooks/useTrans";
import { useLang } from "~/hooks/useLang";
import DisabledButtonTitle from "~/mui/DisabledButtonTitle";
import WhatAbout from "~/components/WhatAbout";
import RemoveDialog from "~/modals/RemoveDialog";
import { getProjectsAndKeysLink } from "~/utils/utils";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	enableAddProject: boolean;
	projects: Array<IClientProject>;
	archivedProjects: Array<IClientProject>;
	errorMsg: string;
	showMotivation: boolean;
	createProject: boolean;
	goToProject: number;
	maxUserProjects: boolean;
	recoverProject: IClientProject;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	enableAddProject: ENABLE_USER_PROJECTS,
	projects: [],
	archivedProjects: [],
	errorMsg: "",
	showMotivation: false,
	createProject: false,
	goToProject: 0,
	maxUserProjects: false,
	recoverProject: null,
};

export default function ProjectsPage() {
	const navigate = useNavigate();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { trans } = useTrans();
	const { lang } = useLang();

	function goToProjectId(projectId: number) {
		// prejdeme na detail projektu
		navigate(getLink("project-dashboard", false, { projectId }));
	}

	function onProjectCreate(projectData: IClientProject) {
		setState(prev => ({
			...prev,
			projects: [
				...prev.projects,
				projectData,
			],
			createProject: false,
			isDirty: false,
			goToProject: projectData.id,
		}));
	}

	async function loadData() {
		updateState(INIT_STATE);

		const projectsData = await apiGetProjects("client", lang);

		setState(prev => ({
			...prev,
			loading: false,
			...projectsData.noErrors
				? {
					projects: projectsData.data.filter(project => !project.archived),
					archivedProjects: projectsData.data.filter(project => project.archived),
					showMotivation: prev.showMotivation === false && projectsData.data.length === 0,
					maxUserProjects: projectsData.data.length >= MAX_USER_PROJECTS,
				}
				: {},
			errorMsg: projectsData.errors.join(", "),
		}));
	}

	async function onProjectRecover(resolve: () => void, reject: (error: string) => void) {
		const archiveData = await apiToggleProjectArchivation("client", state.recoverProject.id, false, lang);

		if (archiveData.noErrors) {
			resolve();
			loadData();
		} else {
			reject(archiveData.errors.join(", "));
		}
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	useEffect(() => {
		if (!state.isDirty && state.goToProject > 0) {
			goToProjectId(state.goToProject);
		}
	}, [state.isDirty, state.goToProject]);

	return <Page title={trans("projects")} checkLogged={true} breadcrumbs={[trans("my.projects")]} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.projects">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ !state.loading && <>
			{ state.enableAddProject && state.showMotivation && <Alert color="info" severity="info" className="projectsPage__alert">
				<p>
					{ trans("projectsPage.motivation") }
					&nbsp;
					<ExternalLink to={DOCUMENTATION_LINK}>
						{ trans("projectsPage.motivation.documentation") }
					</ExternalLink>.
				</p>
			</Alert> }
			{ !state.enableAddProject && <Alert color="info" severity="info" className="projectsPage__alert">
				<p>
					{ trans("projectsPage.createProjectInfo") }
				</p>
			</Alert> }
			{ state.enableAddProject && <TableControls className="projectsPage__tableControls">
				<div></div>
				<div className="projectsPage__createProjectArea">
					<WhatAbout title={trans("tariffWhatAbout.projectTitle")} link={getProjectsAndKeysLink(lang)} />
					<FormControl size="small" className="projectsPage__createProjectArea">
						<DisabledButtonTitle tooltipTitle={trans("limit.user.projects", { count: MAX_USER_PROJECTS })} color="success" variant="contained" onClick={() => updateState({ createProject: true })}
							className="projectsPage__addBtn btn-with-icon">
							<AddIcon /> { trans("createProject") }
						</DisabledButtonTitle>
					</FormControl>
				</div>
			</TableControls> }
			<ProjectsTable items={state.projects} loading={state.loading} />
			{ state.archivedProjects.length > 0 && <>
				<TableControls className="projectsPage__tableControlsArchived">
					<h3>
						{ trans("projectsPage.archivedProjects") }
					</h3>
				</TableControls>
				<ArchivedProjectsTable items={state.archivedProjects} onRecoverProject={recoverProject => updateState({ recoverProject })} />
			</> }
		</> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.createProject && <ClientCreateProjectModal onClose={() => updateState({ createProject: false, isDirty: false })}
			onSave={onProjectCreate} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.recoverProject && <RemoveDialog title={trans("projectsPage.recoverProject")} desc={trans("projectsPage.recoverProjectDesc", { name: state.recoverProject.internalName })}
			onClose={() => updateState({ recoverProject: null })} onRemove={onProjectRecover} removeCaption={trans("projectsPage.recoverProject")} /> }
	</Page>;
}
